// Employee portal database helpers
import { supabase } from './supabase';
import { logger, sanitizeError } from './logger';
import type {
  EmployeeTask,
  EmployeeBatch,
  EmployeeTaskHistory,
  EmployeeTaskInsert,
  EmployeeBatchInsert,
  EmployeeTaskUpdate,
  EmployeeRole,
  TaskStatus,
  TaskFilters,
  TaskSortOptions,
  EmployeePermissions,
  EmployeeProfile,
  EmployeeTaskWithRelations,
} from '@tifpa/shared/types/employee-database.types';

// Re-export EmployeeProfile for use in other modules
export type { EmployeeProfile };

/**
 * Get current user's employee profile and role
 */
export async function getCurrentEmployeeProfile(): Promise<EmployeeProfile | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    logger.error('Error fetching employee profile:', sanitizeError(error));
    return null;
  }

  return data as EmployeeProfile;
}

/**
 * Check if current user has employee access
 */
export async function hasEmployeeAccess(): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_employee_access');

  if (error) {
    logger.error('Error checking employee access:', sanitizeError(error));
    return false;
  }

  return data || false;
}

/**
 * Get current user's employee role
 */
export async function getCurrentEmployeeRole(): Promise<EmployeeRole | null> {
  const { data, error } = await supabase.rpc('get_employee_role');

  if (error) {
    logger.error('Error getting employee role:', error);
    return null;
  }

  return data as EmployeeRole | null;
}

/**
 * Get user permissions based on their employee role (simplified to Manager/Team)
 */
export async function getEmployeePermissions(): Promise<EmployeePermissions> {
  const role = await getCurrentEmployeeRole();

  const defaultPermissions: EmployeePermissions = {
    // Task permissions
    canCreateTasks: false,
    canAssignTasks: false,
    canReviewTasks: false,
    canDeleteTasks: false,
    canManageBatches: false,
    canViewAllTasks: false,

    // General spreadsheet permissions
    canCreateSpreadsheets: false,
    canPublishSpreadsheets: false,
    canDeleteSpreadsheets: false,
    canArchiveSpreadsheets: false,

    // Built-in spreadsheet permissions
    canDeleteBuiltinSpreadsheets: false,
    canEditClientCredentials: false,
  };

  if (!role) return defaultPermissions;

  switch (role) {
    case 'Admin':
    case 'SuperAdmin':
      // Admins and SuperAdmins have full control over everything, including built-in spreadsheets
      return {
        // Task permissions
        canCreateTasks: true,
        canAssignTasks: true,
        canReviewTasks: true,
        canDeleteTasks: true,
        canManageBatches: true,
        canViewAllTasks: true,

        // General spreadsheet permissions
        canCreateSpreadsheets: true,
        canPublishSpreadsheets: true,
        canDeleteSpreadsheets: true,
        canArchiveSpreadsheets: true,

        // Built-in spreadsheet permissions (Admin only)
        canDeleteBuiltinSpreadsheets: true,
        canEditClientCredentials: true,
      };

    case 'Manager':
      // Managers have control over functionality but cannot delete built-in spreadsheets
      return {
        // Task permissions
        canCreateTasks: true,
        canAssignTasks: true,
        canReviewTasks: true,
        canDeleteTasks: true,
        canManageBatches: true,
        canViewAllTasks: true,

        // General spreadsheet permissions
        canCreateSpreadsheets: true,
        canPublishSpreadsheets: true,
        canDeleteSpreadsheets: true,
        canArchiveSpreadsheets: true,

        // Built-in spreadsheet permissions
        canDeleteBuiltinSpreadsheets: false, // Only Admin can delete built-ins
        canEditClientCredentials: true,
      };

    case 'Team':
      // Team members have limited access to built-in spreadsheets
      return {
        // Task permissions
        canCreateTasks: true,
        canAssignTasks: false, // Only managers/admins can assign
        canReviewTasks: true,
        canDeleteTasks: false, // Only managers/admins can delete
        canManageBatches: false,
        canViewAllTasks: true,

        // General spreadsheet permissions
        canCreateSpreadsheets: true,
        canPublishSpreadsheets: true,
        canDeleteSpreadsheets: false, // Only managers/admins can delete
        canArchiveSpreadsheets: false, // Only managers/admins can archive

        // Built-in spreadsheet permissions
        canDeleteBuiltinSpreadsheets: false,
        canEditClientCredentials: false, // Team members cannot edit Client Credentials
      };

    default:
      return defaultPermissions;
  }
}

/**
 * Fetch employee batches
 */
export async function getEmployeeBatches(): Promise<EmployeeBatch[]> {
  const { data, error } = await supabase
    .from('employee_batches')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Error fetching employee batches:', error);
    return [];
  }

  return data || [];
}

/**
 * Create new employee batch
 */
export async function createEmployeeBatch(
  batch: EmployeeBatchInsert
): Promise<EmployeeBatch | null> {
  const { data, error } = await supabase
    .from('employee_batches')
    .insert(batch)
    .select()
    .single();

  if (error) {
    logger.error('Error creating employee batch:', error);
    return null;
  }

  return data;
}

/**
 * Fetch employee tasks with optional filtering and sorting
 */
export async function getEmployeeTasks(
  filters?: TaskFilters,
  sort?: TaskSortOptions
): Promise<EmployeeTaskWithRelations[]> {
  let query = supabase.from('employee_tasks').select(`
      *,
      assignee:profiles!assignee_id(id, name, employee_role),
      created_by_profile:profiles!created_by(id, name, employee_role),
      updated_by_profile:profiles!updated_by(id, name, employee_role),
      reviewed_by_profile:profiles!reviewed_by(id, name, employee_role),
      batch:employee_batches!batch_id(id, name, description)
    `);

  // Apply filters
  if (filters) {
    if (filters.status?.length) {
      query = query.in('status', filters.status);
    }
    if (filters.assignee_id?.length) {
      query = query.in('assignee_id', filters.assignee_id);
    }
    if (filters.batch_id?.length) {
      query = query.in('batch_id', filters.batch_id);
    }
    if (filters.priority?.length) {
      query = query.in('priority', filters.priority);
    }
    if (filters.created_by?.length) {
      query = query.in('created_by', filters.created_by);
    }
    if (filters.due_date_from) {
      query = query.gte('due_date', filters.due_date_from);
    }
    if (filters.due_date_to) {
      query = query.lte('due_date', filters.due_date_to);
    }
  }

  // Apply sorting
  if (sort) {
    query = query.order(sort.field, { ascending: sort.direction === 'asc' });
  } else {
    query = query.order('created_at', { ascending: false });
  }

  const { data, error } = await query;

  if (error) {
    logger.error('Error fetching employee tasks:', error);
    return [];
  }

  return (data || []) as EmployeeTaskWithRelations[];
}

/**
 * Get task by ID with relations
 */
export async function getEmployeeTaskById(
  id: string
): Promise<EmployeeTaskWithRelations | null> {
  const { data, error } = await supabase
    .from('employee_tasks')
    .select(
      `
      *,
      assignee:profiles!assignee_id(id, name, employee_role),
      created_by_profile:profiles!created_by(id, name, employee_role),
      updated_by_profile:profiles!updated_by(id, name, employee_role),
      reviewed_by_profile:profiles!reviewed_by(id, name, employee_role),
      batch:employee_batches!batch_id(id, name, description)
    `
    )
    .eq('id', id)
    .single();

  if (error) {
    logger.error('Error fetching employee task:', error);
    return null;
  }

  return data as EmployeeTaskWithRelations;
}

/**
 * Create new employee task
 */
export async function createEmployeeTask(
  task: EmployeeTaskInsert
): Promise<EmployeeTask | null> {
  const { data, error } = await supabase
    .from('employee_tasks')
    .insert(task)
    .select()
    .single();

  if (error) {
    logger.error('Error creating employee task:', error);
    return null;
  }

  return data;
}

/**
 * Update employee task
 */
export async function updateEmployeeTask(
  id: string,
  updates: EmployeeTaskUpdate
): Promise<EmployeeTask | null> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('employee_tasks')
    .update({
      ...updates,
      updated_by: user.user?.id,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    logger.error('Error updating employee task:', error);
    return null;
  }

  return data;
}

/**
 * Delete employee task (managers only)
 */
export async function deleteEmployeeTask(id: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('employee_tasks')
    .delete()
    .eq('id', id)
    .select();

  if (error) {
    logger.error('Error deleting employee task:', error);
    logger.error('Error details:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    throw error; // Throw the error so we can see the full details
  }

  // Check if any rows were actually deleted
  if (!data || data.length === 0) {
    return false;
  }

  return true;
}

/**
 * Update task status
 */
export async function updateTaskStatus(
  id: string,
  status: TaskStatus
): Promise<EmployeeTask | null> {
  return updateEmployeeTask(id, { status });
}

/**
 * Assign task to user
 */
export async function assignTask(
  id: string,
  assignee_id: string
): Promise<EmployeeTask | null> {
  return updateEmployeeTask(id, { assignee_id });
}

/**
 * Get task history
 */
export async function getTaskHistory(
  task_id: string
): Promise<EmployeeTaskHistory[]> {
  const { data, error } = await supabase
    .from('employee_task_history')
    .select('*')
    .eq('task_id', task_id)
    .order('timestamp', { ascending: false });

  if (error) {
    logger.error('Error fetching task history:', error);
    return [];
  }

  return data || [];
}

/**
 * Get task statistics by batch
 */
export async function getTaskStatsByBatch(batch_id?: string) {
  let query = supabase.from('employee_task_stats').select('*');

  if (batch_id) {
    query = query.eq('batch_id', batch_id);
  }

  const { data, error } = await query;

  if (error) {
    logger.error('Error fetching task stats:', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Get all employees (users with employee roles)
 */
export async function getEmployees(): Promise<EmployeeProfile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .not('employee_role', 'is', null)
    .order('name', { nullsFirst: false });

  if (error) {
    logger.error('Error fetching employees:', error);
    return [];
  }

  return (data as EmployeeProfile[]) || [];
}

/**
 * Set user's employee role (managers only)
 */
export async function setEmployeeRole(
  user_id: string,
  employee_role: EmployeeRole | null
): Promise<boolean> {
  const { error } = await supabase
    .from('profiles')
    .update({ employee_role })
    .eq('id', user_id);

  if (error) {
    logger.error('Error setting employee role:', error);
    return false;
  }

  return true;
}

/**
 * Check if user can edit Client Credentials spreadsheet
 * Only Admins and Managers can edit Client Credentials
 */
export async function canEditClientCredentials(): Promise<boolean> {
  const permissions = await getEmployeePermissions();
  return permissions.canEditClientCredentials;
}

/**
 * Check if user can delete built-in spreadsheets
 * Only Admins can delete built-in spreadsheets
 */
export async function canDeleteBuiltinSpreadsheets(): Promise<boolean> {
  const permissions = await getEmployeePermissions();
  return permissions.canDeleteBuiltinSpreadsheets;
}

/**
 * Check if user can manage spreadsheet access
 * Admins and Managers can manage all spreadsheets
 * Creators can manage their own user-created spreadsheets
 * Only Admins and Managers can manage builtin spreadsheets
 */
export async function canManageSpreadsheetAccess(
  spreadsheet?: any
): Promise<boolean> {
  const role = await getCurrentEmployeeRole();
  const currentUserId = (await supabase.auth.getUser()).data.user?.id;

  // No access without role or user ID
  if (!role || !currentUserId) return false;

  // SuperAdmins, Admins and Managers can manage all spreadsheets
  if (
    role === 'SuperAdmin' ||
    role === 'Admin' ||
    role === 'Manager' ||
    (spreadsheet && spreadsheet.creator_id === currentUserId)
  ) {
    return true;
  }

  // For builtin spreadsheets, only SuperAdmin/Admin/Manager can manage
  if (spreadsheet && (spreadsheet.is_builtin || spreadsheet.builtin_key)) {
    return false;
  }

  // For user-created spreadsheets, creators can manage their own
  if (spreadsheet && spreadsheet.creator_id === currentUserId) {
    return true;
  }

  return false;
}

/**
 * Check if user can grant access to a specific spreadsheet
 * Considers built-in spreadsheet restrictions and creator permissions
 */
export async function canGrantAccessToSpreadsheet(
  spreadsheet: any,
  targetUserRole?: string
): Promise<{ canGrant: boolean; warning?: string }> {
  // Use the enhanced canManageSpreadsheetAccess function
  const canManage = await canManageSpreadsheetAccess(spreadsheet);

  if (!canManage) {
    return { canGrant: false };
  }

  // Check if it's a built-in spreadsheet
  if (spreadsheet.is_builtin || spreadsheet.builtin_key) {
    // Client Credentials - warn about Team member limitations
    if (spreadsheet.builtin_key === 'client_credentials') {
      const warning =
        targetUserRole === 'Team'
          ? 'Warning: Team members cannot edit Client Credentials, even with access. They will have read-only access.'
          : undefined;

      return { canGrant: true, warning };
    }
  }

  return { canGrant: true };
}

/**
 * Internal function to get base permissions without archive check
 */
async function getBaseSpreadsheetPermissions(
  spreadsheet: any,
  userRole?: string,
  userId?: string
): Promise<{
  canView: boolean;
  canEdit: boolean;
  canEditAllColumns: boolean;
  editableColumns?: string[];
  restrictions: string[];
}> {
  const currentRole = userRole || (await getCurrentEmployeeRole());
  const currentUserId = userId || (await supabase.auth.getUser()).data.user?.id;
  const restrictions: string[] = [];

  if (!currentRole || !currentUserId) {
    return {
      canView: false,
      canEdit: false,
      canEditAllColumns: false,
      restrictions: ['No employee access'],
    };
  }

  // Check if it's a Master spreadsheet (original builtin templates)
  if (spreadsheet.is_master) {
    // Define which master spreadsheets Managers can edit
    const managerEditableMasterSpreadsheets = [
      'client_credentials',
      'sales_tax_payroll_logins',
      'tifpa_workflow',
      'annual_sales_taxes',
      'annual_1099_llc_filing',
      'business_personal_extensions',
      'incoming_email_drop_off_filing',
    ];

    // Master spreadsheets can be edited by SuperAdmin and Admin
    if (currentRole === 'SuperAdmin' || currentRole === 'Admin') {
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    } else if (currentRole === 'Manager') {
      // Managers can edit certain master spreadsheets
      const canManagerEdit = managerEditableMasterSpreadsheets.includes(
        spreadsheet.builtin_key
      );

      if (canManagerEdit) {
        return {
          canView: true,
          canEdit: true,
          canEditAllColumns: true,
          restrictions: [],
        };
      } else {
        // Managers get read-only access to other Master spreadsheets
        return {
          canView: true,
          canEdit: false,
          canEditAllColumns: false,
          restrictions: [
            'Only SuperAdmins and Admins can edit this Master spreadsheet',
          ],
        };
      }
    } else if (currentRole === 'Team') {
      // Team members need explicit access grants for Master spreadsheets
      try {
        const { data: hasAccess, error } = await supabase.rpc(
          'has_spreadsheet_access',
          { user_uuid: currentUserId, target_spreadsheet_id: spreadsheet.id }
        );

        if (error) {
          return {
            canView: false,
            canEdit: false,
            canEditAllColumns: false,
            restrictions: [
              'Access control not configured - Team members blocked from Master spreadsheets',
            ],
          };
        }

        if (!hasAccess) {
          return {
            canView: false,
            canEdit: false,
            canEditAllColumns: false,
            restrictions: [
              'Team members require explicit access grants for Master spreadsheets',
            ],
          };
        }

        // Team member has explicit access - full edit access
        return {
          canView: true,
          canEdit: true,
          canEditAllColumns: true,
          restrictions: [],
        };
      } catch (accessError) {
        return {
          canView: false,
          canEdit: false,
          canEditAllColumns: false,
          restrictions: [
            'Unable to verify access - Team members blocked from Master spreadsheets',
          ],
        };
      }
    } else {
      // Unknown role - deny access
      return {
        canView: false,
        canEdit: false,
        canEditAllColumns: false,
        restrictions: ['Unknown role - access denied'],
      };
    }
  }

  // Check if it's a builtin spreadsheet (including copies and test versions)
  if (spreadsheet.is_builtin || spreadsheet.builtin_key) {
    // Debug logging for test spreadsheets
    if (spreadsheet.builtin_key?.includes('_test_v2')) {
      logger.log('[DEBUG] Permission Check for Test Spreadsheet:', {
        builtin_key: spreadsheet.builtin_key,
        is_builtin: spreadsheet.is_builtin,
        currentRole,
        currentUserId,
      });
    }

    // For Manager, Admin, and SuperAdmin: grant automatic access to ALL builtins
    if (
      currentRole === 'Manager' ||
      currentRole === 'Admin' ||
      currentRole === 'SuperAdmin'
    ) {
      if (spreadsheet.builtin_key?.includes('_test_v2')) {
        logger.log('[DEBUG] Granting edit access to', currentRole);
      }
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    }

    // For Team members: Check specific builtin types
    if (currentRole === 'Team') {
      // Client Credentials and Sales Tax & Payroll Logins require explicit
      // access grants for Team members (Manager+ only otherwise)
      const requiresExplicitAccess =
        spreadsheet.builtin_key === 'client_credentials' ||
        spreadsheet.builtin_key === 'sales_tax_payroll_logins' ||
        spreadsheet.builtin_key === 'sales-tax-payroll-logins' ||
        spreadsheet.builtin_key?.startsWith('sales_tax_payroll_logins_copy_');
      if (requiresExplicitAccess) {
        try {
          const { data: hasAccess, error } = await supabase.rpc(
            'has_spreadsheet_access',
            { user_uuid: currentUserId, target_spreadsheet_id: spreadsheet.id }
          );

          if (error || !hasAccess) {
            return {
              canView: false,
              canEdit: false,
              canEditAllColumns: false,
              restrictions: [
                'Team members require explicit access grants for this spreadsheet',
              ],
            };
          }

          // Team member has explicit access - full edit access
          return {
            canView: true,
            canEdit: true,
            canEditAllColumns: true,
            restrictions: [],
          };
        } catch (accessError) {
          return {
            canView: false,
            canEdit: false,
            canEditAllColumns: false,
            restrictions: [
              'Unable to verify access - Team members blocked from this spreadsheet',
            ],
          };
        }
      }

      // For other builtins (including test versions), Team members can edit
      // but are restricted by column-level permissions (see columnPermissions.ts)
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: false,
        restrictions: [],
      };
    }
  }

  // SuperAdmin can view all published spreadsheets
  if (currentRole === 'SuperAdmin') {
    if (spreadsheet.status === 'published') {
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    }
    // For unpublished spreadsheets, SuperAdmin needs explicit access or to be the creator
    if (spreadsheet.creator_id === currentUserId) {
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    }

    // Check for explicit access grant
    try {
      const { data: hasAccess, error } = await supabase.rpc(
        'has_spreadsheet_access',
        { user_uuid: currentUserId, target_spreadsheet_id: spreadsheet.id }
      );

      if (!error && hasAccess) {
        return {
          canView: true,
          canEdit: true,
          canEditAllColumns: true,
          restrictions: [],
        };
      }
    } catch (accessError) {
      logger.error('Error checking access:', accessError);
    }

    return {
      canView: false,
      canEdit: false,
      canEditAllColumns: false,
      restrictions: [
        'SuperAdmin can only view published spreadsheets or spreadsheets with explicit access',
      ],
    };
  }

  // Admin, Manager, and Team members: Check if they're the creator first, then check explicit access grants
  if (
    currentRole === 'Admin' ||
    currentRole === 'Manager' ||
    currentRole === 'Team'
  ) {
    // If user is the creator, they get automatic access
    if (spreadsheet.creator_id === currentUserId) {
      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    }

    // Non-creator users need explicit access grants
    try {
      const { data: hasAccess, error } = await supabase.rpc(
        'has_spreadsheet_access',
        { user_uuid: currentUserId, target_spreadsheet_id: spreadsheet.id }
      );

      if (error) {
        // If the function doesn't exist, deny access
        return {
          canView: false,
          canEdit: false,
          canEditAllColumns: false,
          restrictions: [
            'Access control not configured - Users require explicit access grants',
          ],
        };
      }

      if (!hasAccess) {
        return {
          canView: false,
          canEdit: false,
          canEditAllColumns: false,
          restrictions: [
            'Users require explicit access grants for spreadsheets they did not create',
          ],
        };
      }

      return {
        canView: true,
        canEdit: true,
        canEditAllColumns: true,
        restrictions: [],
      };
    } catch (accessError) {
      return {
        canView: false,
        canEdit: false,
        canEditAllColumns: false,
        restrictions: [
          'Unable to verify access - Users require explicit access grants',
        ],
      };
    }
  }

  // For other roles or edge cases, deny access
  return {
    canView: false,
    canEdit: false,
    canEditAllColumns: false,
    restrictions: ['Access denied - insufficient permissions'],
  };
}

/**
 * Get effective permissions for a user on a specific spreadsheet
 * Wrapper that checks archive status and overrides canEdit if archived
 */
export async function getEffectiveSpreadsheetPermissions(
  spreadsheet: any,
  userRole?: string,
  userId?: string
): Promise<{
  canView: boolean;
  canEdit: boolean;
  canEditAllColumns: boolean;
  editableColumns?: string[];
  restrictions: string[];
}> {
  // Get base permissions
  const basePermissions = await getBaseSpreadsheetPermissions(
    spreadsheet,
    userRole,
    userId
  );

  // If spreadsheet is archived, override edit permissions
  if (spreadsheet.is_archived) {
    return {
      ...basePermissions,
      canEdit: false,
      canEditAllColumns: false,
      editableColumns: [],
      restrictions: [
        'This spreadsheet is archived and locked from editing',
        ...basePermissions.restrictions.filter(
          r => r !== 'This spreadsheet is archived and locked from editing'
        ),
      ],
    };
  }

  return basePermissions;
}
